import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  MapPin,
  Briefcase,
  Edit,
  Link,
  X,
  FileText,
  Zap,
  Search,
  Plus,
  DollarSign,
  Clock,
  Languages,
  User,
  GraduationCap,
  Building,
  Calendar,
  Users,
  Award,
  Globe,
  Target,
  TrendingUp,
  Hash,
  Mail,
  Link2,
  Home,
  BarChart3,
  IndianRupee,
  ArrowLeft
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { editSubJob, getSubJobDetails } from "../../services/jobsapi";
import toast from "react-hot-toast";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader } from "../../components/ui/card";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface JobData {
  id: string;
  title: string;
  description: string;
  aiEnhancedDescription?: string;
  companyName:string;
  status: string;
  location: string;
  workType: string;
  employmentType: string;
  category?: string;
  skills: {
    requiredSkills: string[];
    preferredSkills: string[];
    niceToHaveSkills: string[];
  };
  minSalary: number;
  maxSalary: number;
  currency: string;
  noOfOpenings: number;
  postedBy: { id: string; username: string; email: string };
  assignmentsHistory: any[];
  createdAt: string;
  updatedAt: string;
  paymentFrequency: string;
  commissionType: string;
  commissionAmount: number;
  hiringNeed: string;
  tenureInDays: number;
  shiftTimings: string;
  languageRequirements: string[];
  minExperience: number;
  maxExperience: number;
  minAge: number;
  maxAge: number;
  qualificationRequirements: string[];
  suggestedKeywordsForSeo: string[];
  locationLink: string;
  addressDetails: Record<string, any>;
  subJobId:string;
}

const JobPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [jobData, setJobData] = useState<JobData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditForm, setShowEditForm] = useState(false);
  const [formData, setFormData] = useState<JobData | null>(null);
  const [skillInput, setSkillInput] = useState("");
  const [keywordInput, setKeywordInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const skillsList = ["React", "JavaScript", "TypeScript", "Node.js", "Python"];

  useEffect(() => {
    const fetchJob = async () => {
      try {
        if (!id) throw new Error("Job ID is missing");
        const response = await getSubJobDetails(id);
        setJobData(response.data.job);
        setFormData(response.data.job);
      } catch (err) {
        console.error("Error fetching job:", err);
        toast.error("Failed to fetch job details");
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [id]);

  const formatSalary = (
    min: number,
    max: number,
    currency: string,
    frequency: string
  ) => {
    if (min === 0 && max === 0) return "Salary not specified";
    if (currency === "N/A") return "Salary not specified";
    return `₹ ${min.toLocaleString()} - ${max.toLocaleString()}`;
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'published': return 'bg-emerald-100 text-emerald-700';
      case 'draft': return 'bg-amber-100 text-amber-700';
      case 'closed': return 'bg-rose-100 text-rose-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getWorkTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'remote': return <Globe className="w-5 h-5 text-gray-500" />;
      case 'on-site': return <Building className="w-5 h-5 text-gray-500" />;
      case 'hybrid': return <Target className="w-5 h-5 text-gray-500" />;
      default: return <Briefcase className="w-5 h-5 text-gray-500" />;
    }
  };

  const copyLink = () => {
    const urlToCopy = `${process.env.REACT_APP_PUBLIC_BASE_URL}/jobs/${jobData?.title.replaceAll(" ","-")}/${jobData?.subJobId}`
    navigator.clipboard.writeText(urlToCopy);
    toast.success("Job link copied to clipboard!");
  };

  const openEditForm = () => {
    if (jobData) {
      setFormData({ ...jobData, skills: { ...jobData.skills } });
      setShowEditForm(true);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!formData) return;
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleRemoveSkill = (skill: string) => {
    if (!formData) return;
    setFormData({
      ...formData,
      skills: {
        ...formData.skills,
        requiredSkills: formData.skills.requiredSkills.filter(
          (s) => s !== skill
        ),
      },
    });
  };

  const handleAddKeyword = () => {
    if (!formData || !keywordInput) return;
    if (formData.suggestedKeywordsForSeo.includes(keywordInput)) {
      setError("Keyword already added");
      setTimeout(() => setError(null), 3000);
      setKeywordInput("");
      return;
    }
    setFormData({
      ...formData,
      suggestedKeywordsForSeo: [
        ...formData.suggestedKeywordsForSeo,
        keywordInput,
      ],
    });
    setKeywordInput("");
  };

  const handleRemoveKeyword = (keyword: string) => {
    if (!formData) return;
    setFormData({
      ...formData,
      suggestedKeywordsForSeo: formData.suggestedKeywordsForSeo.filter(
        (k) => k !== keyword
      ),
    });
  };

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>,
    callback: () => void
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      callback();
    }
  };

  // Helper function to check if HTML content has actual text (not just tags)
  const hasValidContent = (htmlContent: string) => {
    if (!htmlContent) return false;
    // Remove HTML tags and check if there's actual text content
    const textContent = htmlContent.replace(/<[^>]*>/g, '').trim();
    return textContent.length > 0;
  };

  const handleEditJob = async () => {
    if (!formData || !id) return;
    try {
      if (!formData.title || !hasValidContent(formData.description) || !formData.location) {
        toast.error("Please fill in all required fields");
        return;
      }
      const data = {
        title: formData.title,
        description: formData.description,
        location: formData.location,
        keywordsForSeo: formData.suggestedKeywordsForSeo,
      };
      const response = await editSubJob(id, data);
      setJobData(response.data.job);
      setShowEditForm(false);
      toast.success("Job updated successfully!");
      // Refresh the job data after edit
      const refreshedData = await getSubJobDetails(id);
      setJobData(refreshedData.data.job);
    } catch (err) {
      console.error("Error updating job:", err);
      toast.error("Failed to update job");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-orange-600 mx-auto"></div>
          <p className="text-gray-600 font-semibold">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!jobData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-bold text-gray-900">Job Not Found</h2>
          <p className="text-gray-600">The job you're looking for doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-gray-700 hover:text-gray-900 hover:bg-white flex items-center gap-2 rounded-lg transition-all"
        >
          <ArrowLeft className="h-5 w-5" />
          Back to Jobs
        </Button>

        {/* Header Card */}
        <Card className="bg-white shadow-xl rounded-2xl border border-gray-200 mb-6 overflow-hidden">
          <CardHeader className="p-8 border-b border-gray-100">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="flex items-start gap-6 flex-1">
                <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center border-2 border-gray-300">
                  <Building className="h-10 w-10 text-gray-500" />
                </div>
                <div className="space-y-3 flex-1">
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">{jobData.title}</h1>
                  <div className="flex flex-wrap items-center gap-3">
                    <Badge className="bg-blue-100 text-blue-800 border-blue-200 px-3 py-1">
                      <Building className="h-3 w-3 mr-1" />
                      {jobData.companyName}
                    </Badge>
                    <Badge className="bg-gray-100 text-gray-800 border-gray-200 px-3 py-1">
                      <MapPin className="h-3 w-3 mr-1" />
                      {jobData.location}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="lg"
                  className="flex items-center gap-2 bg-orange-500 text-white border-0 hover:bg-orange-600 shadow-lg font-medium"
                  onClick={openEditForm}
                >
                  <Edit className="h-5 w-5" /> Edit Job
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="flex items-center gap-2 bg-gray-600 text-white border-0 hover:bg-gray-700 shadow-lg font-medium"
                  onClick={copyLink}
                >
                  <Link className="h-5 w-5" /> Share
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Stats */}
            <Card className="bg-white shadow-lg rounded-xl border border-gray-200">
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <Users className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-900">{jobData.noOfOpenings}</p>
                    <p className="text-xs text-gray-600 mt-1">Openings</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <Briefcase className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                    <p className="text-sm font-bold text-gray-900">{jobData.employmentType}</p>
                    <p className="text-xs text-gray-600 mt-1">Type</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <Globe className="h-6 w-6 text-green-600 mx-auto mb-2" />
                    <p className="text-sm font-bold text-gray-900">{jobData.workType}</p>
                    <p className="text-xs text-gray-600 mt-1">Work Mode</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <Clock className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                    <p className="text-sm font-bold text-gray-900">{jobData.hiringNeed || "N/A"}</p>
                    <p className="text-xs text-gray-600 mt-1">Hiring Need</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Job Description */}
            <Card className="bg-white shadow-lg rounded-xl border border-gray-200">
              <CardHeader className="border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <FileText className="h-5 w-5 text-orange-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Job Description</h2>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div
                  className="prose prose-sm max-w-none text-gray-700 leading-relaxed
                    [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:space-y-1
                    [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:space-y-1
                    [&_li]:mb-1
                    [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-3 [&_h1]:mt-4
                    [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mb-2 [&_h2]:mt-3
                    [&_h3]:text-base [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:mt-2
                    [&_p]:mb-3
                    [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-600
                    [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono
                    [&_pre]:bg-gray-100 [&_pre]:p-3 [&_pre]:rounded [&_pre]:overflow-x-auto
                    [&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800
                    [&_strong]:font-bold
                    [&_em]:italic
                    [&_u]:underline
                    [&_s]:line-through"
                  dangerouslySetInnerHTML={{ __html: jobData.aiEnhancedDescription || jobData.description || "No description provided." }}
                />
              </CardContent>
            </Card>

            {/* Skills & Requirements */}
            <Card className="bg-white shadow-lg rounded-xl border border-gray-200">
              <CardHeader className="border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Target className="h-5 w-5 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Skills & Requirements</h2>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {/* Required Skills */}
                {jobData.skills?.requiredSkills && jobData.skills.requiredSkills.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <Zap className="h-4 w-4 text-orange-600" />
                      Required Skills
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {jobData.skills.requiredSkills.map((skill, index) => (
                        <Badge key={`required-${index}`} className="bg-orange-100 text-orange-800 border-orange-200 px-3 py-1.5 font-medium">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preferred Skills */}
                {jobData.skills?.preferredSkills && jobData.skills.preferredSkills.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <Award className="h-4 w-4 text-blue-600" />
                      Preferred Skills
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {jobData.skills.preferredSkills.map((skill, index) => (
                        <Badge key={`preferred-${index}`} className="bg-blue-100 text-blue-800 border-blue-200 px-3 py-1.5 font-medium">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Nice to Have Skills */}
                {jobData.skills?.niceToHaveSkills && jobData.skills.niceToHaveSkills.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-green-600" />
                      Nice to Have
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {jobData.skills.niceToHaveSkills.map((skill, index) => (
                        <Badge key={`nice-${index}`} className="bg-green-100 text-green-800 border-green-200 px-3 py-1.5 font-medium">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {!jobData.skills?.requiredSkills?.length &&
                 !jobData.skills?.preferredSkills?.length &&
                 !jobData.skills?.niceToHaveSkills?.length && (
                  <p className="text-gray-500 text-center py-4">No skills specified.</p>
                )}

                {/* Experience & Education */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                  <div className="flex items-start gap-3">
                    <Briefcase className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Experience</p>
                      <p className="text-gray-900">
                        {jobData.minExperience !== undefined 
                          ? `${jobData.minExperience} - ${jobData.maxExperience} years`
                          : "Not specified"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <GraduationCap className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Education</p>
                      <p className="text-gray-900">{jobData.qualificationRequirements?.join(", ") || "Not specified"}</p>
                    </div>
                  </div>
                </div>

                {/* Age Range */}
                {(jobData.minAge || jobData.maxAge) && (
                  <div className="flex items-start gap-3 pt-2">
                    <Users className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Age Range</p>
                      <p className="text-gray-900">{jobData.minAge} - {jobData.maxAge} years</p>
                    </div>
                  </div>
                )}

                {/* Languages */}
                {jobData.languageRequirements && jobData.languageRequirements.length > 0 && (
                  <div className="flex items-start gap-3 pt-2">
                    <Globe className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Languages Required</p>
                      <p className="text-gray-900">{jobData.languageRequirements.join(", ")}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* SEO Keywords */}
            {jobData.suggestedKeywordsForSeo && jobData.suggestedKeywordsForSeo.length > 0 && (
              <Card className="bg-white shadow-lg rounded-xl border border-gray-200">
                <CardHeader className="border-b border-gray-100 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Target className="h-5 w-5 text-yellow-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">SEO Keywords</h2>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="flex flex-wrap gap-2">
                    {jobData.suggestedKeywordsForSeo.map((keyword, index) => (
                      <Badge key={index} className="bg-yellow-50 text-yellow-800 border-yellow-200 px-3 py-1.5 font-medium">
                        {keyword}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Salary & Compensation */}
            <Card className="bg-white shadow-lg rounded-xl border border-gray-200">
              <CardHeader className="border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Compensation</h2>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg border border-green-100">
                  <p className="text-sm text-gray-600 mb-1">Salary Range</p>
                  <p className="text-2xl font-bold text-green-700">
                    {formatSalary(jobData.minSalary, jobData.maxSalary, jobData.currency, jobData.paymentFrequency)}
                  </p>
                  {jobData.paymentFrequency && (
                    <p className="text-sm text-gray-600 mt-1">per {jobData.paymentFrequency}</p>
                  )}
                </div>

                {/* {jobData.commissionAmount !== undefined && jobData.commissionAmount > 0 && (
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-100">
                    <p className="text-sm text-gray-600 mb-1">Commission</p>
                    <p className="text-xl font-bold text-orange-700">
                      {jobData.commissionType === "fixed"
                        ? `${jobData.currency} ${((jobData.commissionAmount / 100) * 70).toFixed(2)} Per Joining`
                        : `${((jobData.commissionAmount / 100) * 50).toFixed(2)}% of Annual CTC`}
                    </p>
                  </div>
                )} */}

                {/* {jobData.tenureInDays !== undefined && jobData.tenureInDays > 0 && (
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <Clock className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="text-sm text-gray-600">Tenure</p>
                      <p className="text-lg font-semibold text-gray-900">{jobData.tenureInDays} Days</p>
                    </div>
                  </div>
                )} */}

                {jobData.shiftTimings && (
                  <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                    <Clock className="h-5 w-5 text-purple-600" />
                    <div>
                      <p className="text-sm text-gray-600">Shift Timings</p>
                      <p className="text-lg font-semibold text-gray-900">{jobData.shiftTimings}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Company Details */}
            <Card className="bg-white shadow-lg rounded-xl border border-gray-200">
              <CardHeader className="border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Building className="h-5 w-5 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Company Details</h2>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Company Name</p>
                  <p className="text-lg font-semibold text-gray-900">{jobData.companyName}</p>
                </div>

                {(Object.keys(jobData.addressDetails).length > 0 || jobData.location) && (
                  <div className="pt-3 border-t border-gray-100">
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Address</p>
                        <div className="space-y-1 text-gray-700">
                          {jobData.location && <p>{jobData.location}</p>}
                          {Object.entries(jobData.addressDetails).map(([key, value]) => 
                            value && <p key={key}>{value}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {jobData.locationLink && (
                  <a
                    href={jobData.locationLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium transition-colors"
                  >
                    <MapPin className="h-4 w-4" />
                    View on Map
                  </a>
                )}
              </CardContent>
            </Card>

            {/* Job Information */}
            <Card className="bg-white shadow-lg rounded-xl border border-gray-200">
              <CardHeader className="border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Calendar className="h-5 w-5 text-gray-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Job Information</h2>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Posted On</span>
                  <span className="font-medium text-gray-900">
                    {new Date(jobData.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Last Updated</span>
                  <span className="font-medium text-gray-900">
                    {new Date(jobData.updatedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                  </span>
                </div>
                {jobData.category && (
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-gray-600">Category</span>
                    <Badge className="bg-gray-100 text-gray-800 font-medium">{jobData.category}</Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8 flex">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="text-gray-700 hover:text-gray-900 hover:bg-white flex items-center gap-2 px-6 py-3 rounded-lg shadow-md transition-all"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Jobs
          </Button>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditForm && formData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 bg-orange-600 text-white">
              <h3 className="text-xl font-bold">Edit Job Details</h3>
              <button 
                onClick={() => setShowEditForm(false)}
                className="p-1 hover:bg-orange-700 rounded-full transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center">
                  <Briefcase className="w-4 h-4 mr-2 text-orange-600" />
                  Job Title <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData?.title || ""}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-500 transition-all"
                  placeholder="Enter job title (e.g., Senior React Developer)"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center">
                  <FileText className="w-4 h-4 mr-2 text-orange-600" />
                  Job Description <span className="text-red-500 ml-1">*</span>
                </label>
                <ReactQuill
                  value={formData?.description || ""}
                  onChange={(value) => setFormData(prev => prev ? { ...prev, description: value } : null)}
                  placeholder="Describe the job responsibilities, requirements, and expectations..."
                  className="w-full rounded-lg"
                  theme="snow"
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      ['link'],
                      ['clean']
                    ],
                    clipboard: {
                      matchVisual: false,
                    }
                  }}
                  formats={[
                    'header',
                    'bold', 'italic', 'underline',
                    'list', 'bullet',
                    'link'
                  ]}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center">
                  <MapPin className="w-4 h-4 mr-2 text-orange-600" />
                  Location <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData?.location || ""}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-500 transition-all"
                  placeholder="Enter location (e.g., New York, NY or Remote)"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center">
                  <Search className="w-4 h-4 mr-2 text-orange-600" />
                  SEO Keywords <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, handleAddKeyword)}
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-500 transition-all"
                    placeholder="Add SEO keyword (e.g., remote, frontend)"
                  />
                  <button
                    type="button"
                    onClick={handleAddKeyword}
                    className="px-4 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-all"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {formData?.suggestedKeywordsForSeo && formData.suggestedKeywordsForSeo.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.suggestedKeywordsForSeo.map((keyword) => (
                      <div
                        key={keyword}
                        className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium"
                      >
                        #{keyword}
                        <button
                          type="button"
                          onClick={() => handleRemoveKeyword(keyword)}
                          className="ml-2 p-1 hover:bg-orange-200 rounded-full"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {error && <p className="text-red-500 text-sm">{error}</p>}

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
                <button
                  onClick={() => setShowEditForm(false)}
                  className="flex-1 py-2.5 px-4 border border-gray-200 rounded-lg text-gray-700 font-semibold hover:bg-gray-100 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditJob}
                  className="flex-1 py-2.5 px-4 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-all"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobPage;